import React, { useRef, useCallback, memo, forwardRef } from "react";
import {
  Input as ChakraInput,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  InputGroup,
  InputRightElement,
  InputLeftElement,
} from "@chakra-ui/react";
import { FastField, Field, useFormikContext } from "formik";

import { removeWhitespaceFromString, exists } from "utils";

const Input = forwardRef((props, ref) => {
  const { setFieldValue } = useFormikContext();
  const localRef = useRef();
  const refToUse = ref || localRef; // to allow this Input to work with Google Places Autocomplete api, we need to be able to optionally pass in a ref

  /* istanbul ignore next */
  const handlePaste = useCallback(
    /**
     * @param {ClipboardEvent} event
     */
    (event) => {
      let newValue = props.value;
      let maxLength = props.maxLength;

      // Grab the pasted value from the clipboard
      const pastedValue = event.clipboardData.getData("text");
      const sanitizedValue = removeWhitespaceFromString(pastedValue);

      // Grab the current cursor position(s)
      const { selectionStart, selectionEnd } = refToUse.current;
      if (selectionStart !== selectionEnd) {
        // User has a portion of the input field selected, so we remove that part (just like a regular 'paste' would)
        newValue = newValue.slice(0, selectionStart) + newValue.slice(selectionEnd);
      }

      // Place our sanitized pasted value where the cursor is currently located
      newValue = `${newValue.slice(0, selectionStart)}${sanitizedValue}${newValue.slice(
        selectionStart
      )}`;

      // Check if maxLength is a number. If it is then
      // only get the chars till maxLength
      if (exists(maxLength) && !isNaN(maxLength)) {
        newValue = newValue.slice(0, maxLength);
      }

      setFieldValue(props.name, newValue);

      event.preventDefault();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.value]
  );

  return (
    <ChakraInput
      {...props}
      ref={refToUse}
      onPaste={handlePaste}
      data-testid={`textfield-${props.id}`}
    />
  );
});

const TextField = forwardRef(
  ({ name, label, type = "text", helperText, isOptional = false, validate, ...rest }, ref) => {
    return (
      <FastField name={name} validate={validate}>
        {({ field, meta }) => {
          const isInvalid = meta.touched && meta.error;
          return (
            <FormControl isInvalid={isInvalid} isRequired={!isOptional}>
              {label && (
                <FormLabel htmlFor={name} requiredIndicator={false}>
                  {label}
                </FormLabel>
              )}
              <Input {...field} id={name} type={type} {...rest} ref={ref} />

              {!isInvalid && !!helperText ? (
                <FormHelperText>{helperText}</FormHelperText>
              ) : (
                <FormErrorMessage>{meta.error}</FormErrorMessage>
              )}
            </FormControl>
          );
        }}
      </FastField>
    );
  }
);

export const TextFieldGroup = forwardRef(
  (
    {
      name,
      label,
      type = "text",
      helperText,
      isOptional = false,
      validate,
      rightElement,
      leftElement,
      ...rest
    },
    ref
  ) => {
    return (
      <Field name={name} validate={validate}>
        {({ field, meta }) => {
          const isInvalid = meta.touched && meta.error;
          return (
            <FormControl isInvalid={isInvalid} isRequired={!isOptional}>
              {label && (
                <FormLabel htmlFor={name} requiredIndicator={false}>
                  {label}
                </FormLabel>
              )}
              <InputGroup>
                {leftElement && <InputLeftElement>{leftElement}</InputLeftElement>}
                <ChakraInput
                  {...field}
                  id={name}
                  data-testid={`textfieldgroup-${name}`}
                  type={type}
                  {...rest}
                  ref={ref}
                  value={field.value || ""} // so we don't pass in undefined to chakra text input
                />
                {rightElement && <InputRightElement>{rightElement}</InputRightElement>}
              </InputGroup>

              {!isInvalid && !!helperText ? (
                <FormHelperText>{helperText}</FormHelperText>
              ) : (
                <FormErrorMessage>{meta.error}</FormErrorMessage>
              )}
            </FormControl>
          );
        }}
      </Field>
    );
  }
);

export default memo(TextField);
